import React, { useEffect, useState } from "react";
import moment from "moment";
import styles from "./LoyaltyTransactions.module.scss";
import {
  UserLoyaltyTransactionResponse,
  LoyaltyTransactionFilters,
  LOYALTY_TRANSACTION_TYPE_LABELS,
} from "../../types/loyaltyTypes";
import {
  fetchUserLoyaltyTransactions,
  calculateTotalPoints,
} from "../../store/loyalty-actions";
import Button from "../../components/UI/Buttons/Button/Button";
import SimpleModal from "../../components/UI/Modals/SimpleModal/SimpleModal";
import Calendar from "react-calendar";
import filterIll from "../../assets/icons/filter-ilustration.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow-icon.svg";

const LoyaltyTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<
    UserLoyaltyTransactionResponse[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Filter state
  const [filters, setFilters] = useState<LoyaltyTransactionFilters>({
    createdFrom: moment().startOf("month").toDate(),
    createdTo: moment().toDate(),
    page: 0,
    numOfReturns: 50,
  });

  // Fetch transactions when filters change
  useEffect(() => {
    loadTransactions();
  }, [filters.createdFrom, filters.createdTo]);

  const loadTransactions = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchUserLoyaltyTransactions(filters);
      setTransactions(data);
    } catch (err) {
      setError("Greška pri učitavanju aktivnosti");
      console.error("Error loading loyalty transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return moment(dateString).format("DD.MM.YYYY HH:mm");
  };

  const formatPoints = (points: number): string => {
    const pointsNum = Number(points);
    return pointsNum >= 0 ? `+${pointsNum}` : `${pointsNum}`;
  };

  const getPointsClassName = (points: number): string => {
    return Number(points) >= 0 ? styles.positivePoints : styles.negativePoints;
  };

  return (
    <div className={styles.loyaltyTransactionsWrap}>
      {/* Filters Modal */}
      {showFilters && (
        <FiltersModal
          hideModal={() => setShowFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Moje aktivnosti</h2>
        <div className={styles.periodInfo}>
          {moment(filters.createdFrom).format("DD.MM.YYYY")} -{" "}
          {moment(filters.createdTo).format("DD.MM.YYYY")}
        </div>
        {/* Filters Icon */}
        <div className={styles.filtersIcon} onClick={() => setShowFilters(true)}>
          <img src={filterIll} alt="Filter" />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className={styles.loadingState}>Učitavanje transakcija...</div>
      )}

      {/* Error State */}
      {error && <div className={styles.errorState}>{error}</div>}

      {/* Transactions List */}
      {!loading && !error && (
        <>
          {transactions.length === 0 ? (
            <div className={styles.noResults}>
              Nema aktivnosti za izabrani period
            </div>
          ) : (
            <ul className={styles.transactionsList}>
              {transactions.map((transaction) => (
                <li
                  key={transaction.id}
                  className={`${styles.transactionItem}${
                    Number(transaction.loyaltyPoints) < 0
                      ? ` ${styles.negativeTransaction}`
                      : ""
                  }`}
                >
                  <details
                    id={`transaction-${transaction.id}`}
                    aria-controls={`transaction-${transaction.id}-content`}
                    className={styles.transactionItemInner}
                    title="Detalji aktivnosti"
                  >
                    <summary className={styles.transactionSummary}>
                      <div className={styles.transactionDate}>
                        {formatDate(transaction.created)}
                      </div>
                      {transaction.orderNumber && (
                        <div className={styles.transactionOrderNumber}>
                          #{transaction.orderNumber}
                        </div>
                      )}
                      <div className={styles.transactionType}>
                        {LOYALTY_TRANSACTION_TYPE_LABELS[transaction.type]}
                      </div>
                      <ArrowIcon />
                    </summary>

                    <div
                      id={`transaction-${transaction.id}-content`}
                      aria-labelledby={`transaction-${transaction.id}`}
                      className={styles.transactionDetails}
                    >
                      {transaction.name && (
                        <div className={styles.transactionName}>
                          {transaction.name}
                        </div>
                      )}

                      <div
                        className={`${
                          styles.transactionPoints
                        } ${getPointsClassName(transaction.loyaltyPoints)}`}
                      >
                        {formatPoints(transaction.loyaltyPoints)}
                      </div>
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          )}

          {/* Footer with totals */}
          {transactions.length > 0 && (
            <div className={styles.footerWrap}>
              <div className={styles.footerElements}>Ukupno:</div>
              <div className={styles.footerElements}>
                {calculateTotalPoints(transactions)}
              </div>
              <div className={styles.footerElements}>
                {transactions.length} aktivnosti
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Filters Modal Component
interface FiltersModalProps {
  hideModal: () => void;
  filters: LoyaltyTransactionFilters;
  setFilters: (filters: LoyaltyTransactionFilters) => void;
}

const FiltersModal: React.FC<FiltersModalProps> = ({
  hideModal,
  filters,
  setFilters,
}) => {
  return (
    <SimpleModal
      className={styles.filtersModal}
      backdropClickHandler={hideModal}
      show={false}
    >
      <div className={styles.segment}>
        <div className={styles.header}>Period</div>
        <Calendar
          selectRange={true}
          returnValue="range"
          value={[filters.createdFrom, filters.createdTo]}
          onChange={(val) => {
            if (val && Array.isArray(val) && val[0] && val[1]) {
              setFilters({
                ...filters,
                createdFrom: val[0] as Date,
                createdTo: val[1] as Date,
              });
            }
          }}
          maxDate={new Date()}
          locale="sr"
          className={styles.calendarWrap}
        />
      </div>

      <Button buttonType="blue" onClick={hideModal}>
        Primeni
      </Button>
    </SimpleModal>
  );
};

export default LoyaltyTransactions;
